import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import QuestionSection from "../../ui/QuestionSection/QuestionSection";
import CommonFooter from "../../CommonFooter/CommonFooter";
import CommonButton from "../../ui/CommonButton/CommonButton";
import classes from "./ReagentsConsumables.module.css";
import { env } from "../../../constants/env";
import axios from "axios";

const ReagentsConsumables = () => {
  const { labId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [currentSet, setCurrentSet] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editable, setEditable] = useState(user?.role === "ASSESSOR");
  const [isfetching, setIsFetching] = useState(false);

  const userRole = user?.role;

  const handleAnswerChange = (questionId, score, comment, points) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { score, comment, points },
    }));
  };

  useEffect(() => {
    if (!labId) return;
    fetchAccreditation();
  }, [labId, token, user._id]);

  const fetchAccreditation = async () => {
    setIsFetching(true);
    try {
      const res = await axios.get(`${env.kataApiUrl}/api/accreditation`, {
        params: {
          labId,
          section: "Reagents Consumables",
          name: "SADCAS F134-B",
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.success && res.data.accreditation) {
        const accreditation = res.data.accreditation;

        // Check if current user is the assigned auditor
        if (accreditation.auditorId !== user._id && userRole !== "ASSESSOR") {
          setEditable(false);
        }

        if (accreditation.answers && accreditation.answers.length) {
          const mapped = accreditation.answers.reduce((acc, a) => {
            acc[a.questionId] = {
              score: a.score || "",
              comment: a.comment || "",
              points: a.points || 0,
            };
            return acc;
          }, {});
          setAnswers(mapped);
          setEditable(false); // already submitted → read-only
        } else {
          setEditable(true); // no answers → edit mode
        }
      }
    } catch (err) {
      console.error("Failed to fetch accreditation:", err);
    } finally {
      setIsFetching(false);
    }
  };

  const questionSets = [
    {
      questions1: [
        {
          questionId: "Q1",
          content:
            "a. Does the laboratory store reagents and consumables according to the manufacturers’ specifications and monitor the environmental conditions where relevant?",
          number: "6.6.2",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q2",
          content:
            "b. When the laboratory is not the receiving facility, do they verify that the receiving facility has adequate storage and handling capabilities to maintain supplies in a manner that prevents damage and deterioration?",
          number: "6.6.3",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q3",
          content:
            "c. Is new formulation of examination kits with changes in reagents or procedure, or new lot or shipment verified for performance before use or before release of results, as appropriate?",
          number: "6.6.4",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q4",
          content:
            "d. Does the inventory management system established by the laboratory segregate reagents and consumables that have been accepted for use from those that have not been inspected or accepted?",
          number: "6.6.5",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q5",
          content:
            "e. Are instructions for use of reagents and consumables, including those provided by the manufacturers, readily available?",
          number: "6.6.6",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q6",
          content:
            "f. Are adverse incidents and accidents (attributed to specific reagents or consumables) investigated and reported to manufacturer and/or supplier and appropriate authorities, as required?",
          number: "6.6.6",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q7",
          content:
            "g. Does the laboratory have procedures for responding to any manufacturer’s recall or other notice and taking actions recommended by the manufacturer?",
          number: "6.6.7",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
        {
          content: "h. Are the following records available:",
          number: "",
          hideOptions: true,
        },
        {
          questionId: "Q8",
          content: "i. Identity of the reagent or consumable",
          number: "",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q9",
          content: "ii. Manufacturer’s name, and batch code/ lot number",
          number: "",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q10",
          content:
            "iii. Date of receipt and condition when received, expiry date, date of first use and where applicable date when taken out of service",
          number: "",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q11",
          content:
            "iv. Records that confirmed the reagent’s or consumable’s initial and ongoing acceptance for use",
          number: "",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
      ],
      questions2: [
        {
          questionId: "Comment1",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
      ],
      render: (onAnswerChange, answers) => (
        <>
          <QuestionSection
            topQuestion={false}
            accreditation
            addWidth
            showPointsField={false}
            questions={questionSets[0].questions1}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
          <QuestionSection
            topQuestion={false}
            showQuestion={false}
            showEmptyNumber={false}
            accreditation
            showPointsField={false}
            questions={questionSets[0].questions2}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
        </>
      ),
    },
  ];

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet((prev) => prev + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet((prev) => prev - 1);
  };

  const handleDone = async () => {
    setIsSubmitting(true);
    try {
      const allQuestions = questionSets.flatMap((set) =>
        Object.keys(set)
          .filter((k) => k.startsWith("questions"))
          .flatMap((k) => set[k])
      );

      const answersArray = Object.entries(answers).map(([questionId, data]) => {
        const questionContent =
          allQuestions.find((q) => q.questionId === questionId)?.content || "";

        return {
          questionId,
          content: questionContent,
          score: data.score || undefined,
          comment: data.comment,
          points: data.points,
        };
      });

      const response = await fetch(`${env.kataApiUrl}/api/accreditation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: "SADCAS F134-B",
          labId: labId,
          section: "Reagents Consumables",
          auditorId: user._id,
          answers: answersArray,
        }),
      });

      if (!response.ok) throw new Error("Submission failed");
      fetchAccreditation();
      toast.success("Updated successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to submit assessment");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isfetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
    <div
      className={`${classes.wrapper} ${!editable ? classes.fromParams : ""}`}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div
          className="d-flex align-items-center gap-2 mb-4"
          onClick={() => navigate(-1)}
          role="button"
        >
          <IoIosArrowRoundBack size={35} />
          <h2 className="m-0">Clause 7.2.3: Reagents and consumables</h2>
        </div>

        {userRole === "ASSESSOR" && !editable && (
          <CommonButton
            className={classes.editButton}
            onClick={() => setEditable(true)}
            title="Edit"
          />
        )}
      </div>

      {!editable ? (
        <div className={classes.infoText}>
          ✅ Your SADCAS B - Clause 7.2.3: Reagents and consumables has been
          submitted.
        </div>
      ) : (
        questionSets[currentSet].render(handleAnswerChange, answers)
      )}

      {editable && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => setEditable(false)}
          currentSet={currentSet}
          questionSets={questionSets}
          doneText={isSubmitting ? "Submitting..." : "Update"}
          disabled={isSubmitting}
        />
      )}
    </div>
  );
};

export default ReagentsConsumables;
